import { MARKETPLACE_META, type MarketplaceCode, type SafetyReviewField, type SettingsTemplate } from "./types";
import { inspectSafetySchema, safetyRecord, type SafetySchema } from "./safetySchema";

export type SafetyPresetCategory = "lego" | "fragrance" | "other";
export interface SafetyPresetValues extends Record<string, unknown> {
  version: 1;
  category: SafetyPresetCategory;
  productType: string;
  attributes: Record<string, unknown>;
  schemaFingerprints: Record<string, string>;
  note: string;
  sourceSku?: string;
  sourceProduct?: string;
}
interface Context {
  fields: SafetyReviewField[];
  attributes: Record<string, unknown>;
  marketplace: string;
  productType?: string;
}
export interface SafetyPresetResult {
  attributes: Record<string, unknown>;
  appliedFields: string[];
  notices: string[];
}
const battery = (name: string) => /batter|battery|lithium/i.test(name);
const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (safetyRecord(value)) return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${canonical(value[key])}`).join(",")}}`;
  return JSON.stringify(value) ?? "null";
}
function fingerprint(schema: SafetySchema): string {
  const text = canonical(schema);
  let hash = 2166136261;
  for (let index = 0; index < text.length; index++) hash = Math.imul(hash ^ text.charCodeAt(index), 16777619);
  return `v1:${text.length}:${(hash >>> 0).toString(16)}`;
}

export function compatibleSafetyPreset(template: SettingsTemplate, context: { marketplace: string; productType?: string }): boolean {
  return template.kind === "safety" && template.values.version === 1
    && Boolean(context.productType?.trim())
    && template.marketplace === context.marketplace
    && typeof template.values.productType === "string"
    && template.values.productType.trim().toUpperCase() === context.productType?.trim().toUpperCase()
    && safetyRecord(template.values.attributes);
}

/** Capture explicit editable leaves; apply them against the current live target, never saved identifiers. */
function mapValue(raw: SafetySchema, root: SafetySchema, incoming: unknown, current: unknown, capture: boolean, path: string, marketplace: string, notices: string[] = [], depth = 0): unknown {
  if (depth > 24) throw Error(`${path}: Die Struktur ist zu tief verschachtelt.`);
  const inspected = inspectSafetySchema(raw, root);
  if (inspected.reason || "const" in inspected.schema) return capture ? undefined : current;
  const schema = inspected.schema;
  if (incoming === undefined || incoming === null) return capture ? undefined : current;
  const invalid = (reason: string): never => { throw Error(`${path}: ${reason}`); };
  if (inspected.type === "object") {
    if (!safetyRecord(incoming) || !safetyRecord(schema.properties)) return invalid("Die Objektstruktur passt nicht zum aktuellen Schema.");
    if (!capture && current != null && !safetyRecord(current)) return invalid("Die vorhandene Struktur bleibt erhalten.");
    const old = safetyRecord(current) ? current : {};
    const marketplaceId = MARKETPLACE_META[marketplace as MarketplaceCode]?.marketplaceId;
    if ((incoming.marketplace_id != null && incoming.marketplace_id !== marketplaceId)
      || (!capture && old.marketplace_id != null && old.marketplace_id !== marketplaceId)) return invalid("Die eingebetteten Angaben gehören zu einem anderen Marktplatz.");
    if (!capture && typeof incoming.language_tag === "string" && typeof old.language_tag === "string" && incoming.language_tag !== old.language_tag) return invalid("Die Sprachangabe weicht von den aktuellen Angaben ab.");
    const next: Record<string, unknown> = capture ? {} : structuredClone(old);
    for (const [name, child] of Object.entries(schema.properties)) {
      if (!safetyRecord(child) || battery(name)) continue;
      const node = inspectSafetySchema(child, root);
      if (node.reason || "const" in node.schema) {
        if (!capture && next[name] === undefined && /^(marketplace_id|language_tag)$/.test(name)) {
          if ("const" in node.schema) next[name] = structuredClone(node.schema.const);
          else if ("default" in node.schema) next[name] = structuredClone(node.schema.default);
        }
        continue;
      }
      if (!capture && /^(marketplace_id|language_tag)$/.test(name) && next[name] !== undefined) continue;
      let candidate = incoming[name];
      if (!capture && candidate === undefined && /^(marketplace_id|language_tag)$/.test(name)) {
        candidate = node.schema.default;
        const id = MARKETPLACE_META[marketplace as MarketplaceCode]?.marketplaceId;
        if (name === "marketplace_id" && candidate === undefined && Array.isArray(node.schema.enum) && node.schema.enum.includes(id)) candidate = id;
      }
      const value = mapValue(child, root, candidate, old[name], capture, `${path}.${name}`, marketplace, notices, depth + 1);
      if (value !== undefined) next[name] = value;
    }
    if (!Object.keys(next).length) return undefined;
    if (!capture && Array.isArray(schema.required) && schema.required.some(key => typeof key === "string" && next[key] === undefined)) return invalid("Für Pflichtangaben fehlen aktuelle Werte; nichts wurde geraten.");
    return next;
  }
  if (inspected.type === "array") {
    if (!Array.isArray(incoming) || !safetyRecord(schema.items)) return invalid("Die Listenstruktur passt nicht zum aktuellen Schema.");
    if (!capture && current != null && !Array.isArray(current)) return invalid("Die vorhandene Struktur bleibt erhalten.");
    const old = Array.isArray(current) ? current : [];
    const item = inspectSafetySchema(schema.items, root);
    const properties = safetyRecord(item.schema.properties) ? item.schema.properties : {};
    const valueSchema = safetyRecord(properties.value) ? inspectSafetySchema(properties.value, root) : undefined;
    const wrappedEnum = item.type === "object" && valueSchema && !valueSchema.reason && Array.isArray(valueSchema.schema.enum)
      && Object.entries(properties).every(([name, child]) => /^(value|marketplace_id|language_tag)$/.test(name)
        || (safetyRecord(child) && (Boolean(inspectSafetySchema(child, root).reason) || "const" in child)));
    if (!capture && wrappedEnum) {
      const selectors = Array.isArray(schema.selectors) ? schema.selectors.filter(key => typeof key === "string" && key !== "value") as string[] : [];
      const keys = [...new Set(["value", "language_tag", ...selectors])];
      const matches = (left: Record<string, unknown>, right: Record<string, unknown>) => keys.every(key => right[key] === undefined || equal(left[key], right[key]));
      const next = structuredClone(old);
      const used = new Set<number>();
      for (let index = 0; index < incoming.length; index++) {
        const candidate = incoming[index];
        if (!safetyRecord(candidate)) return invalid("Die gespeicherte Auswahl ist kein eindeutiger Datensatz.");
        if (incoming.slice(0, index).some(previous => safetyRecord(previous) && matches(previous, candidate) && matches(candidate, previous))) return invalid("Mehrere gespeicherte Auswahlen sind nicht eindeutig zuordenbar.");
        const matching = old.flatMap((entry, position) => safetyRecord(entry) && matches(entry, candidate) ? [position] : []);
        if (matching.length > 1 || (matching.length && used.has(matching[0]))) return invalid("Vorhandene Auswahlen sind nicht eindeutig nach Wert und Sprache zuordenbar.");
        const position = matching[0];
        const value = mapValue(schema.items as SafetySchema, root, candidate, position === undefined ? undefined : old[position], false, `${path}[${index}]`, marketplace, notices, depth + 1);
        if (value === undefined) continue;
        if (position === undefined) next.push(value);
        else { next[position] = value; used.add(position); }
      }
      if (typeof schema.minItems === "number" && next.length < schema.minItems || typeof schema.maxItems === "number" && next.length > schema.maxItems) return invalid("Die vollständige Auswahl passt nicht zu den aktuellen Listengrenzen.");
      if (old.some((_entry, index) => !used.has(index))) notices.push(`${path}: Weitere vorhandene Auswahlen und ihre Metadaten bleiben erhalten. Entferne unzutreffende Angaben nach Prüfung im Ländereditor.`);
      return next;
    }
    // Multiple object records can carry product-specific IDs. There is no safe
    // positional correspondence between two products; require manual editing.
    if (!capture && item.type === "object" && old.length && (old.length > 1 || incoming.length > 1)) return invalid("Mehrere Datensätze sind nicht eindeutig zuordenbar; bitte einzeln bearbeiten.");
    if (!capture && item.type === "object" && old.length && !incoming.length) return invalid("Vorhandene Datensätze und ihre Metadaten bleiben erhalten.");
    const next = incoming.map((value, index) => mapValue(schema.items as SafetySchema, root, value, old[index], capture, `${path}[${index}]`, marketplace, notices, depth + 1)).filter(value => value !== undefined);
    if (!capture && item.type !== "object" && Array.isArray(item.schema.enum)) {
      next.push(...old.filter(value => !(item.schema.enum as unknown[]).some(option => equal(option, value)) && !next.some(option => equal(option, value))));
    }
    if ((!capture && typeof schema.minItems === "number" && next.length < schema.minItems) || (typeof schema.maxItems === "number" && next.length > schema.maxItems)) return invalid("Die vollständige Auswahl passt nicht zu den aktuellen Listengrenzen.");
    return next;
  }
  const kind = inspected.type;
  if ((kind === "boolean" && typeof incoming !== "boolean")
    || (kind === "string" && typeof incoming !== "string")
    || ((kind === "number" || kind === "integer") && (typeof incoming !== "number" || !Number.isFinite(incoming) || (kind === "integer" && !Number.isInteger(incoming))))) return invalid("Der Wert hat nicht den aktuellen Datentyp.");
  if (Array.isArray(schema.enum) && !schema.enum.some(value => Object.is(value, incoming))) return invalid("Der gespeicherte Wert ist aktuell nicht zulässig.");
  if (typeof incoming === "string") {
    if ((typeof schema.minLength === "number" && incoming.length < schema.minLength) || (typeof schema.maxLength === "number" && incoming.length > schema.maxLength)) return invalid("Der Text verletzt die aktuellen Längenvorgaben.");
    if (typeof schema.pattern === "string") {
      try { if (!new RegExp(schema.pattern).test(incoming)) return invalid("Der Text verletzt das aktuelle Muster."); }
      catch { return invalid("Das aktuelle Textmuster kann nicht sicher geprüft werden."); }
    }
  }
  if (typeof incoming === "number" && ((typeof schema.minimum === "number" && incoming < schema.minimum) || (typeof schema.maximum === "number" && incoming > schema.maximum))) return invalid("Die Zahl liegt außerhalb der aktuellen Grenzen.");
  return structuredClone(incoming);
}

export function captureSafetyPreset({ fields, attributes, marketplace, productType, category, note = "", sellerSku, productTitle }: Context & {
  category: SafetyPresetCategory; note?: string; sellerSku?: string; productTitle?: string;
}): { values: SafetyPresetValues; appliedFields: string[]; notices: string[] } {
  const values: SafetyPresetValues = { version: 1, category, productType: productType?.trim().toUpperCase() ?? "", attributes: {}, schemaFingerprints: {}, note, ...(sellerSku ? { sourceSku: sellerSku } : {}), ...(productTitle ? { sourceProduct: productTitle } : {}) };
  const notices: string[] = [];
  if (!values.productType || !MARKETPLACE_META[marketplace as MarketplaceCode]) return { values, appliedFields: [], notices: ["Aktueller Produkttyp und Marktplatz sind zum Speichern erforderlich."] };
  for (const field of fields) {
    if (battery(field.name)) { notices.push(`${field.label}: Batterieangaben werden für jeden Artikel separat geprüft und nicht als Vorlage gespeichert.`); continue; }
    if (!field.editable) continue;
    const current = Object.hasOwn(attributes, field.name) ? attributes[field.name] : field.currentValue;
    try {
      const value = mapValue(field.schema, field.schema, current, undefined, true, field.label, marketplace, notices);
      if (value !== undefined) { values.attributes[field.name] = value; values.schemaFingerprints[field.name] = fingerprint(field.schema); }
    } catch (reason) { notices.push(String(reason instanceof Error ? reason.message : reason)); }
  }
  return { values, appliedFields: Object.keys(values.attributes), notices };
}

export function applySafetyPreset({ template, fields, attributes, marketplace, productType, targetMarketplaces = [marketplace] }: Context & {
  template: SettingsTemplate; targetMarketplaces?: string[];
}): SafetyPresetResult {
  const result: SafetyPresetResult = { attributes: structuredClone(attributes), appliedFields: [], notices: [] };
  if (!compatibleSafetyPreset(template, { marketplace, productType })) { result.notices.push("Die Vorlage gehört zu einem anderen Marktplatz oder Produkttyp."); return result; }
  if (targetMarketplaces.length !== 1 || targetMarketplaces[0] !== marketplace) { result.notices.push("Gespeicherte Vorlagen bitte im Editor für ein einzelnes Land anwenden. So bleiben Länderangaben und Sprachen getrennt."); return result; }
  const saved = template.values.attributes as Record<string, unknown>;
  for (const [name, incoming] of Object.entries(saved)) {
    const field = fields.find(item => item.name === name);
    if (battery(name)) { result.notices.push("Batterieangaben werden nicht aus Vorlagen übernommen. Bitte am konkreten Artikel prüfen."); continue; }
    if (!field?.editable) { result.notices.push(`${name}: Aktuell kein bearbeitbares Sicherheitsfeld.`); continue; }
    const inspected = inspectSafetySchema(field.schema);
    if (inspected.reason || "const" in inspected.schema) { result.notices.push(`${field.label}: ${inspected.reason ?? "Der Wert ist von Amazon festgelegt."}`); continue; }
    const fingerprints = template.values.schemaFingerprints;
    if (safetyRecord(fingerprints) && fingerprints[name] !== undefined && fingerprints[name] !== fingerprint(field.schema)) { result.notices.push(`${field.label}: Das Amazon-Schema hat sich geändert; bitte die Vorlage anhand der aktuellen Felder prüfen.`); continue; }
    const current = Object.hasOwn(attributes, name) ? attributes[name] : field.currentValue;
    try {
      const value = mapValue(field.schema, field.schema, incoming, current, false, field.label, marketplace, result.notices);
      if (value !== undefined && !equal(current, value)) { result.attributes[name] = value; result.appliedFields.push(name); }
    } catch (reason) { result.notices.push(String(reason instanceof Error ? reason.message : reason)); }
  }
  return result;
}
